import axios from 'axios';
import history from '../../history';
import axiosInstance from '../utils/axios';
import {_openModal} from './modals';
import lazy from 'lazy.js';
import debug from 'debug';
import store, {_removeAllModals, _removeNotification, _setBrand} from '../store';
import {_clearDefaultContainerID} from '../../advisor/reducers/defaultContainerID';
import {_getConfig} from './config';
import {goHome, checkV3ReleaseModals} from '../utils/utils';
import cache from '../utils/swrCache';
import {PORTAL_NAME} from '../../config.json';
import qs from 'qs';

const log = debug('circleblack:user');
const basePath = (PORTAL_NAME === 'widgets') ? '.' : '';

const validRoles = {
  ops: ['DATAOPS', 'PRODOPS', 'CLIENTOPS'],
  advisor: ['ADVISOR', 'PRODMGR', 'FIRMMGR', 'PRODSUPP', 'BRANCHMGR', 'ASSOC', 'CPLO'],
  investor: ['CUSTOMER', 'TESTER', 'DEMO', 'ADVISOR', 'FIRMMGR', 'BRANCHMGR', 'ASSOC', 'CPLO'],
};

const validateRoleForPortal = role => {
  if (!validRoles[PORTAL_NAME].includes(role)) {
    throw Error('Unauthorized for portal.');
  }
};

const checkVersion = () => axios.get(basePath + '/version.json')
  .then(res => {
    if (process.env.REACT_APP_HASH !== res.data.hash) {
      window.location.reload(true);
    }
  })
  .catch(() => log('Unable to verify version'));

export const splitEntitlement = (ent) => {
  const parts = ent.split(':');
  return {
    group: parts.shift(),
    access: parts.pop(),
    feature: parts.join(':'),
  };
};

const session = {
  token: '',
  ssoToken: null,
  entitlements: localStorage.getItem('entitlements')
    && lazy(JSON.parse(localStorage.getItem('entitlements'))).map(splitEntitlement)
    || lazy([]),
  profile: {},
  preferences: {
    riskPartner: 'riskalyze',
    financialPlanningPartner: 'emoney',
  },
  agent: {},
  entryPage: {
    path: '',
    redirect: false,
  },
  pendingMFA: false,
  partner: null,
  runAsInvestor: null,
  aggregationStatus: 'SUCCESS',
};

log('Session entitlements', session.entitlements);

/* --- actions ---*/
const LOGIN_USER = 'LOGIN_USER';
const LOGOUT_USER = 'LOGOUT_USER';
const SET_ENTITLEMENTS = 'SET_ENTITLEMENTS';
const SET_ENTRY_PAGE = 'SET_ENTRY_PAGE';
const SET_PARTNER = 'SET_PARTNER';
const SET_PROFILE = 'SET_PROFILE';
const SET_PREFERENCES = 'SET_PREFERENCES';
const SET_SSO_TOKEN = 'SET_SSO_TOKEN';
const SET_TOKEN = 'SET_TOKEN';
const ACT_AS_USER = 'ACT_AS_USER';
const RUN_AS_INVESTOR = 'RUN_AS_INVESTOR';
const CLEAR_AGENT = 'CLEAR_AGENT';
const CLEAR_PENDING_MFA = 'CLEAR_PENDING_MFA';
const SET_AGGREGATION_STATUS = 'SET_AGGREGATION_STATUS';

/* -- action creators -- */
const _destroySession = () => ({
  type: LOGOUT_USER,
  session,
});

const _setPartner = partner => ({
  type: SET_PARTNER,
  partner,
});

const _setToken = token => ({
  type: SET_TOKEN,
  token,
});

export const _setSsoToken = ssoToken => ({
  type: SET_SSO_TOKEN,
  ssoToken,
});

export const _setEntryPath = entry => dispatch => {
  dispatch({
    type: SET_ENTRY_PAGE,
    entry,
  });
};

export const _setProfile = profile => ({
  type: SET_PROFILE,
  profile,
});

export const _setPreferences = preferences => ({
  type: SET_PREFERENCES,
  preferences,
});

export const _setAggregationStatus = aggregationStatus => dispatch => {
  dispatch({type: SET_AGGREGATION_STATUS, aggregationStatus});
};

export const _actAs = (advisorID, skipNavigation) => dispatch => axiosInstance.get(`/users/${advisorID}`)
  .then(res => {
    localStorage.setItem('runAs', advisorID);
    localStorage.setItem('agent', JSON.stringify(session.profile));
    localStorage.setItem('agentEntitlements', localStorage.getItem('entitlements'));

    // ETODO Go to the user's home page for refinitiv
    axios.defaults.headers.common['cb-run-as'] = advisorID;

    return axiosInstance.get('/user/profile')
      .then(profile => {
        log(
          'acting as',
          {runAs: advisorID, entitlements: lazy(res.data.entitlements).map(splitEntitlement).toArray(), profile: profile.data}
        );
        if (!skipNavigation) {
          goHome(profile.data.role);
        }
        dispatch(_setBrand(profile.data.brand));
        return dispatch({
          type: ACT_AS_USER,
          session: {runAs: advisorID, entitlements: lazy(res.data.entitlements).map(splitEntitlement), profile: profile.data},
        });
      })
      .then(() => dispatch(_getConfig(1)))
      .then(checkVersion);
  })
  .catch(function error(err) {
    dispatch(_openModal({
      type: 'Error',
      title: 'Failed',
      err,
    }));
  });

export const _gatherEntitlements = (refresh, skipModal) => dispatch => {
  const user = store.getState().session;

  return axiosInstance.get('/user/entitlements')
    .then(function entitlementSuccess(res) {
      dispatch({
        type: SET_ENTITLEMENTS,
        entitlements: lazy(res.data).map(splitEntitlement),
      });
      localStorage.setItem('entitlements', JSON.stringify(res.data));
      if (refresh) {
        return;
      }
      const runAs = localStorage.getItem('runAs');
      if (runAs) {
        localStorage.removeItem('runAs');
      }
      return axiosInstance.get('/user/profile')
        .then(profileRes => {
          if (user.ssoToken) {
            validateRoleForPortal(profileRes.data.role);
            dispatch(_setBrand(profileRes.data.brand)); // Like createSession, this is technically the wrong brand
          }
          dispatch(_setProfile(profileRes.data));
          dispatch(_setPartner(profileRes.data.sessionPartner));
          if (runAs) {
            dispatch(_actAs(runAs, user.entryPage && user.entryPage.redirect));
          }
          if (user.entryPage && user.entryPage.redirect && (PORTAL_NAME !== 'widgets')) {
            if (user.entryPage.path.match(/\/($|\?)/)) {
              goHome(profileRes.data.role);
            } else {
              history.push(user.entryPage.path);
            }
            dispatch(_setEntryPath({redirect: false}));
          } else if (!runAs && (PORTAL_NAME !== 'widgets')) {
            goHome(profileRes.data.role);
          }
          checkVersion();
        });
    })
    .catch(function error(err) {
      localStorage.removeItem('token');
      localStorage.removeItem('entitlements');
      localStorage.removeItem('runAs');
      localStorage.removeItem('initialLoad');
      history.push('/login');
      if (user.ssoToken) {
        dispatch(_destroySession());
        dispatch(_openModal({type: 'Error', title: 'Login Failed', err}));
      } else if (!skipModal) {
        dispatch(_openModal({type: 'Error', title: 'Failed', err}));
      }
    });
};

export const _runsAsInvestorSession = (data) => dispatch => {
  const {agent, user} = data;

  localStorage.setItem('token', data.session.token);
  localStorage.setItem('email', agent.email);
  localStorage.setItem('runAsInvestor', user.id);
  localStorage.setItem('initialLoad', JSON.stringify(true));

  axios.defaults.headers.common = {
    'cb-portal': localStorage.getItem('portal'),
    'cb-email': agent.email,
    'cb-token': data.session.token,
    'cb-run-as': user.id,
  };

  return axiosInstance.get('/user/profile').then(profileRes => {
    localStorage.setItem('brand', JSON.stringify(profileRes.data.brand));

    dispatch(_setToken(data.session.token));
    dispatch(_setBrand(profileRes.data.brand));

    return axiosInstance.get(`users/${user.id}`).then(userRes => {
      dispatch({
        type: RUN_AS_INVESTOR,
        agent: agent,
        profile: profileRes.data,
        entitlements: lazy(userRes.data.entitlements).map(splitEntitlement),
        runAsInvestor: user.id,
      });
      localStorage.setItem('entitlements', JSON.stringify(userRes.data.entitlements));
      goHome(userRes.role);
    });
  }).catch(function error(err) {
    localStorage.removeItem('token');
    localStorage.removeItem('entitlements');
    localStorage.removeItem('runAsInvestor');
    localStorage.removeItem('initialLoad');
    history.push('/login');
    dispatch(_destroySession());
    dispatch(_openModal({type: 'Error', title: 'Single Sign-On Unsuccessful', err}));
  });
};

export const _createSession = (credentials, nextRoute) => dispatch => {
  const {email, password} = credentials;
  const user = store.getState().session;

  const trustedDeviceToken = {};
  if (localStorage.getItem('trustedDeviceToken-' + email)) {
    trustedDeviceToken.trustedDeviceToken = localStorage.getItem('trustedDeviceToken-' + email);
  }
  return axiosInstance.post(
    '/user/sessions',
    {email: email.trim(), password: password ? password.trim() : null, ssoToken: user.ssoToken || null, ...trustedDeviceToken}
  )
    .then(function authorize(res) {
      validateRoleForPortal(res.data.profile.role);

      localStorage.setItem('token', res.data.token);
      localStorage.setItem('email', email);
      localStorage.setItem('brand', JSON.stringify(res.data.profile.brand));
      localStorage.setItem('entitlements', JSON.stringify(res.data.entitlements));
      localStorage.setItem('initialLoad', JSON.stringify(true));

      axios.defaults.headers.common = {
        'cb-token': localStorage.getItem('token'),
        'cb-email': localStorage.getItem('email'),
        'cb-portal': localStorage.getItem('portal'),
      };

      const newSession = {
        token: res.data.token,
        entitlements: lazy(res.data.entitlements).map(splitEntitlement),
        profile: res.data.profile,
        pendingMFA: !!res.data.mfaMessage,
        partner: res.data.partner,
      };

      dispatch({
        type: LOGIN_USER,
        session: newSession,
      });

      dispatch(_setBrand(res.data.profile.brand));
      if (res.data?.mfaMessage) {
        dispatch(_openModal({
          componentName: 'TwoFactorAuth',
          mfaMessage: res.data.mfaMessage,
          email: email,
          session: newSession,
        }));
      } else if (res.data?.owner?.hasTemporaryPassword) {
        history.push('/firstLogin/setPassword?' + qs.stringify({email, token: newSession.token}));
      } else {
        dispatch(_setSsoToken(null));
        history.push(nextRoute || '/');
        if (res.data?.profile?.t3SurveyRequired) {
          dispatch(_openModal({
            componentName: 'T3SurveyModal',
          }));
        }
        checkV3ReleaseModals(res.data?.profile);
        return newSession;
      }
    })
    .catch(function error(err) {
      dispatch(_openModal({
        type: err.response?.data?.error?.code === 118 ? 'AccountLocked' : 'Error',
        title: 'Login Failed',
        err,
      }));
      history.push('/login');
    });
};

export const _setSession = credentials => dispatch => {
  const {email, token, partner} = credentials;
  const user = store.getState().session;

  localStorage.setItem('email', email);
  localStorage.setItem('token', token);
  localStorage.setItem('initialLoad', JSON.stringify(true));

  axios.defaults.headers.common = {
    'cb-token': localStorage.getItem('token'),
    'cb-email': localStorage.getItem('email'),
    'cb-portal': localStorage.getItem('portal'),
  };

  dispatch(_setToken(token));
  dispatch(_setPartner(partner));

  if (!user.ssoToken) {
    window.location.pathname = '/';
    return Promise.resolve();
  }
  return dispatch(_gatherEntitlements())
    .then(() => dispatch(_setSsoToken(null)));
};

export const _changeEmail = profile => dispatch => {
  const s = store.getState().session;
  if (!s.runAs) {
    localStorage.setItem('email', profile.email);
    axios.defaults.headers.common = {
      ...axios.defaults.headers.common,
      'cb-email': profile.email,
    };
  }
  dispatch({
    type: SET_PROFILE,
    profile,
  });
};

export const _resetPassword = email => dispatch => {
  const user = store.getState().session;

  return axiosInstance.post('/user/emails/password/passwordReset', {email, ssoToken: user.ssoToken})
    .then(() => {
      localStorage.setItem('email', email);
    })
    .finally(() => dispatch(_openModal({type: 'MessageModal', title: 'Reset Password', body: 'If you entered a valid email address you will receive an email with instructions to reset your password.'})));
};

export const _clearAgent = () => dispatch => {
  // const user = store.getState().session.agent;
  localStorage.setItem('entitlements', localStorage.getItem('agentEntitlements'));
  localStorage.removeItem('runAs');
  delete axios.defaults.headers.common['cb-run-as'];
  cache.clear();
  dispatch({type: CLEAR_AGENT});
};

export const _clearPendingMFA = () => dispatch => {
  dispatch({type: CLEAR_PENDING_MFA});
};

/* --- dispatchers --- */
export const _closeSession = (options) => dispatch => {
  const opts = options || {};
  cache.clear();
  dispatch(_removeAllModals());
  dispatch(_removeNotification());
  return (opts.skipAPICall ? Promise.resolve() : axiosInstance.delete('/user/session'))
    .then(function deleteSession() {
      localStorage.removeItem('token');
      localStorage.removeItem('entitlements');
      localStorage.removeItem('runAs');
      localStorage.removeItem('initialLoad');
      dispatch(_destroySession());
      delete axios.defaults.headers.common['cb-token'];
      if (!opts.skipRedirect) {
        history.push('/login');
      }
    })
    .catch(err => dispatch(_openModal({type: 'Error', title: 'Failed', err})));
};

export const _closeSessionSSO = ssoToken => dispatch => {
  cache.clear();
  dispatch(_removeAllModals());
  return axiosInstance.delete('/auth/sso', {params: {ssoToken}})
    .then(() => dispatch(_setSsoToken(null)))
    .catch(err => dispatch(_openModal({type: 'Error', title: 'Failed', err})))
    .finally(() => history.push('/login'));
};

export const _switchSession = config => dispatch => {
  let op;
  const user = store.getState().session;

  if (user.agent?.id) {
    dispatch(_clearAgent());
  }
  dispatch(_clearDefaultContainerID());

  if (config.email) {
    // New session must be created
    op = axiosInstance.post('/user/sessions/switch', {email: config.email}, {runAsSelf: true})
      .then(res => dispatch(_closeSession({skipAPICall: true, skipRedirect: true}))
        .then(() => dispatch(_setSession({
          email: res.data.owner.email,
          token: res.data.token,
          partner: res.data.partner,
        }))));
  } else if (config.session) {
    // New session already exists (e.g. MFA flow)
    op = dispatch(_closeSession({skipRedirect: true}))
      .then(() => dispatch(_setSession({
        email: config.session.owner.email,
        token: config.session.token,
        partner: config.session.partner,
      })));
  }
  return op
    .catch(err => dispatch(_openModal({type: 'Error', title: 'Failed', err})));
};

export const isEntitled = (entitlements, requireAll) => {
  const user = store.getState().session;
  let matches = 0;
  if (!user.entitlements || !user.entitlements.findWhere) return false;
  for (const ent of entitlements) {
    if (user.entitlements.findWhere(ent)) {
      if (!requireAll) return true;
      matches++;
    }
  }
  return matches >= entitlements.length;
};

export const isAgentEntitled = (entitlements, requireAll) => {
  const user = store.getState().session;
  let matches = 0;
  if (!user.agent?.entitlements || !user.agent?.entitlements.findWhere) return false;
  for (const ent of entitlements) {
    if (user.agent.entitlements.findWhere(ent)) {
      if (!requireAll) return true;
      matches++;
    }
  }
  return matches >= entitlements.length;
};

export const isAuthorized = roles => {
  const {profile, pendingMFA} = store.getState().session;

  return !pendingMFA && roles.includes(profile.role);
};

export const getRiskPartner = () => {
  const user = store.getState().session;
  if (user.entitlements && user.entitlements.findWhere) {
    const riskalyze = user.entitlements.findWhere({
      group: 'risk',
      feature: 'riskalyze',
      access: 'r',
    });
    const totum = user.entitlements.findWhere({
      group: 'risk',
      feature: 'totum',
      access: 'r',
    });
    const stratifi = user.entitlements.findWhere({
      group: 'risk',
      feature: 'stratifi',
      access: 'r',
    });
    const entitlement = user.entitlements.where({
      group: 'risk',
      access: 'r',
    });
    return riskalyze?.feature
      || totum?.feature
      || stratifi?.feature
      || entitlement?.feature
      || null;
  }
  return null;
};

/* --- reducer ---*/
export default function reducer(state = session, action) {
  let newState = {...state};

  switch (action.type) {
    case LOGIN_USER:
      newState.entitlements = action.session.entitlements;
      newState.token = action.session.token;
      newState.profile = action.session.profile;
      newState.pendingMFA = action.session.pendingMFA;
      newState.partner = action.session.partner;
      break;
    case LOGOUT_USER:
      newState = action.session;
      newState.ssoToken = state.ssoToken;
      break;
    case SET_ENTRY_PAGE:
      if (action.entry.pathname) {
        newState.entryPage.path = action.entry.pathname;
      }
      newState.entryPage.redirect = action.entry.redirect;
      break;
    case SET_ENTITLEMENTS:
      newState.entitlements = action.entitlements;
      break;
    case SET_PARTNER:
      newState.partner = action.partner;
      break;
    case SET_PROFILE:
      newState.profile = action.profile;
      break;
    case SET_PREFERENCES:
      newState.preferences = action.preferences;
      break;
    case SET_SSO_TOKEN:
      newState.ssoToken = action.ssoToken;
      break;
    case SET_TOKEN:
      newState.token = action.token;
      break;
    case RUN_AS_INVESTOR:
      newState.agent = action.agent;
      newState.entitlements = action.entitlements;
      newState.runAsInvestor = action.runAsInvestor;
      newState.profile = action.profile;
      break;
    case ACT_AS_USER:
      newState.agent = state.profile;
      newState.agent.entitlements = state.entitlements;
      newState.entitlements = action.session.entitlements;
      newState.profile = action.session.profile;
      newState.runAs = action.session.runAs;
      break;
    case CLEAR_AGENT:
      newState.entitlements = state.agent.entitlements;
      newState.profile = state.agent;
      newState.agent = undefined;
      newState.runAs = undefined;
      break;
    case CLEAR_PENDING_MFA:
      newState.pendingMFA = false;
      break;
    case SET_AGGREGATION_STATUS:
      newState.aggregationStatus = action.aggregationStatus;
      break;
    default:
      return state;
  }
  return newState;
}
